import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight, Loader2, Volume2 } from "lucide-react";
import { useNarrator } from "@/lib/narrator";

function rectOf(testid) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left - 6, y: r.top - 6, w: r.width + 12, h: r.height + 12 };
}

export default function GuidedTour({ steps, storageKey, rootTestId, onStep, onDone, testId = "guided-tour" }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const narrator = useNarrator();
  const step = steps[i];

  useLayoutEffect(() => {
    onStep?.(step.id);
    const tick = () => setRect(rectOf(step.target));
    const t = setTimeout(tick, 80);
    window.addEventListener("resize", tick);
    return () => { clearTimeout(t); window.removeEventListener("resize", tick); };
  }, [i]); // eslint-disable-line

  useEffect(() => { narrator.speak(step.id, step.text); }, [i]); // eslint-disable-line

  const finish = () => { narrator.stop(); localStorage.setItem(storageKey, "1"); onDone(); };
  const next = () => (i < steps.length - 1 ? setI(i + 1) : finish());

  const full = step.target === rootTestId;
  const card = (() => {
    if (!rect || full) return { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
    const below = rect.y + rect.h + 200 < window.innerHeight;
    const left = Math.min(Math.max(16, rect.x + rect.w / 2 - 190), window.innerWidth - 396);
    return below ? { left, top: rect.y + rect.h + 14 } : { left, top: Math.max(16, rect.y - 200) };
  })();

  return createPortal(
    <div className="fixed inset-0 z-[80]" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40" onClick={next} />
      {rect && !full && (
        <div className="absolute rounded-lg ring-2 ring-amber-400 pointer-events-none transition-[left,top,width,height] duration-300" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, boxShadow: "0 0 0 9999px rgba(0,0,0,.55)" }} />
      )}
      <div className="absolute w-[380px] rounded-xl bg-[#15161a] border border-amber-400/30 shadow-2xl p-5 text-zinc-200 transition-[left,top] duration-300" style={card} data-testid={`tour-step-${step.id}`}>
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[.25em] text-amber-400">Step {i + 1} of {steps.length}</div>
          <button onClick={finish} data-testid="tour-skip" className="text-zinc-500 hover:text-white flex items-center gap-1 text-xs">Skip tour <X className="w-3.5 h-3.5" /></button>
        </div>
        <h3 className="text-lg font-medium mt-2 text-white">{step.title}</h3>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{step.text}</p>
        <div className="flex items-center justify-between mt-5">
          <button onClick={() => narrator.speak(step.id, step.text)} data-testid="tour-replay" className="text-xs text-zinc-400 hover:text-amber-300 flex items-center gap-1.5">
            {narrator.loading === step.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className={`w-3.5 h-3.5 ${narrator.speaking === step.id ? "text-amber-400" : ""}`} />}
            {narrator.speaking === step.id ? "Narrating…" : "Replay"}
          </button>
          <button onClick={next} data-testid="tour-next" className="h-9 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-zinc-900 text-sm font-medium flex items-center gap-1">
            {i < steps.length - 1 ? "Next" : "Start creating"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
